
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, effect, inject, ResourceRef, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { patchState, signalStoreFeature, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AreaCodesEndpoint } from '@pims-endpoints';
import { LocalStorageKeys } from '@shared-utils';
import { catchError, pipe, tap, throttleTime, throwError } from 'rxjs';

export type AreaCodesState = { areaCodes: string[] };
export type GetAreaCodesTriggerRequest = { polling: boolean };

export function withAreaCodes() {
  return signalStoreFeature(
    withState<AreaCodesState>({
      areaCodes: [],
    }),
    withProps(store => ({
      _windowRefAreaCodes: inject(DOCUMENT).defaultView,
      _areaCodesEndpoint: new AreaCodesEndpoint(),
      _getAreaCodesTrigger: signal<GetAreaCodesTriggerRequest | undefined>(undefined),
    })),
    withMethods(store => ({
      setAreaCodes: (areaCodes: string[]) => {
        patchState(store, { areaCodes });
      },
      removeAreaCodes: () => {
        patchState(store, { areaCodes: undefined });
      },
      getAreaCodes: rxMethod<GetAreaCodesTriggerRequest | undefined>(
        pipe(
          throttleTime(500),
          tap((req = { polling: true }) => store._getAreaCodesTrigger.update(() => ({ ...req }))),
        ),
      ),
    })),
    withProps(store => {
      const _areaCodesResource: ResourceRef<string[] | undefined> = rxResource({
        params: () => store._getAreaCodesTrigger(),
        stream: ({params}) => {
          const { polling } = { ...params };

          return store._areaCodesEndpoint.get(polling).pipe(
            tap(areaCodes => {
              store._windowRefAreaCodes?.localStorage.setItem(LocalStorageKeys.AREA_CODES, JSON.stringify(areaCodes));
              patchState(store, { areaCodes });
            }),
            catchError((err: HttpErrorResponse) => {
              store._windowRefAreaCodes?.localStorage.setItem(LocalStorageKeys.AREA_CODES, JSON.stringify([]));
              console.error(err);
              store.removeAreaCodes();
              return throwError(() => Object.assign(new Error('Something went wrong'), err));
            }),
          );
        },
      });

      return {
        areaCodesResource: _areaCodesResource.asReadonly(),
      };
    }),
    withHooks({
      onInit({ areaCodes, _windowRefAreaCodes }) {
        // Effect to synchronize area codes with localStorage
        effect(() => {
          const currentAreaCodes = areaCodes();
          if (_windowRefAreaCodes) {
            if (currentAreaCodes) {
              _windowRefAreaCodes.localStorage.setItem(LocalStorageKeys.AREA_CODES, JSON.stringify(currentAreaCodes));
            } else {
              _windowRefAreaCodes.localStorage.removeItem(LocalStorageKeys.AREA_CODES);
            }
          }
          console.log('localStorage Area Codes sync effect run. Current Area Codes:', currentAreaCodes);
        });
      },
      onDestroy(store) {
        store.getAreaCodes.destroy();
      },
    }),
  );
}
